import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { FluentProvider, QueryProvider } from './providers';
import { AuthProvider, msalInstance } from './auth';
import { MockAuthProvider } from './auth/MockAuthProvider';
import { App } from './App';
import { telemetryService } from './services/telemetryService';
import { reportWebVitals } from './services/webVitalsService';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { getActiveFeatureFlags } from './featureFlags';
import { getAppInsightsConnectionString } from './services/envContext';
import { useWorkflowStore } from './stores/workflowStore';
import { useEditorRunStore } from './stores/editorRunStore';
import './index.css';

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';
// Auth is always real MSAL — mock only for E2E tests.
// VITE_LOCAL_MODE controls runtime routing (localhost vs Azure), not auth.
const ActiveAuthProvider = isE2EMode ? MockAuthProvider : AuthProvider;

// Initialize telemetry before rendering.
//
// Connection string resolution order:
//   1. <meta name="otto-appinsights-conn"> injected by docker-entrypoint.sh
//      from the ACA's APPINSIGHTS_CONN env var. Lets us rotate the AI
//      resource by updating ACA env vars + bouncing revisions — no rebuild.
//   2. import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING baked at build
//      time by Vite. Fallback for local pnpm dev + tests where there's
//      no entrypoint substitution.
telemetryService.initialize({
  connectionString:
    getAppInsightsConnectionString() ?? import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ?? '',
  enabled: import.meta.env.VITE_TELEMETRY_ENABLED !== 'false' && !isE2EMode,
  samplingPercentage: 100,
  environment: __BUILD_ENVIRONMENT__,
});

// Report Web Vitals after telemetry is initialized
reportWebVitals();

// Log active feature flags for debugging/telemetry
const activeFlags = getActiveFeatureFlags();
if (activeFlags.length > 0) {
  telemetryService.trackEvent(
    'app.feature_flags_active',
    { flags: activeFlags.join(',') },
    { count: activeFlags.length },
  );
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Expose the workflow store on `window` in E2E mode only. Playwright tests
// drive insert/delete operations directly against the real store and assert
// on the rendered DOM, instead of depending on UI button positions that may
// shift across redesigns. Static import is safe — `useWorkflowStore` is
// already eagerly loaded by `App.tsx`, and the assignment is gated on
// `isE2EMode` so production builds never expose internal state on `window`.
declare global {
  interface Window {
    __OTTO_WORKFLOW_STORE__?: typeof useWorkflowStore;
    __OTTO_EDITOR_RUN_STORE__?: typeof useEditorRunStore;
  }
}
if (isE2EMode) {
  window.__OTTO_WORKFLOW_STORE__ = useWorkflowStore;
  window.__OTTO_EDITOR_RUN_STORE__ = useEditorRunStore;
}

const renderApp = () => {
  createRoot(rootElement).render(
    <StrictMode>
      <AppErrorBoundary componentName="Root">
        <QueryProvider>
          <FluentProvider>
            <ActiveAuthProvider>
              <App />
            </ActiveAuthProvider>
          </FluentProvider>
        </QueryProvider>
      </AppErrorBoundary>
    </StrictMode>
  );
};

// Pre-initialize MSAL before mounting React. `initialize()` is fast
// (~50ms) and idempotent; running it here means `MsalProvider` always
// mounts with an initialized instance, so children can safely use
// msal-react hooks without seeing an "uninitialized" error. In E2E
// mode we use `MockAuthProvider` and skip MSAL entirely.
//
// Wrapped in `.then()` rather than top-level await because the build
// target (es2020 / Safari 14) doesn't support top-level await.
if (isE2EMode) {
  renderApp();
} else {
  msalInstance.initialize().then(renderApp).catch((err) => {
    console.error('[main] MSAL initialize failed, rendering app anyway:', err);
    renderApp();
  });
}
